import { useMemo } from 'react';
import type { TableColumn } from 'src/components/common/GentlyTable';

const DEFAULT_HIDDEN_KEYS = new Set(['importSource', 'csvRowIndex']);

export interface DynamicMetadataColumnsOptions<
  T extends Record<string, unknown>,
> {
  rows: T[];
  metadataAccessor: (row: T) => Record<string, unknown> | null | undefined;
  hiddenKeys?: Set<string>;
  knownKeys?: string[];
  labelOverrides?: Record<string, string>;
  sortable?: boolean;
}

export interface DynamicMetadataColumnsResult<
  T extends Record<string, unknown>,
> {
  metadataKeys: string[];
  metadataColumns: TableColumn<T>[];
  flattenRow: (row: T) => T;
}

function formatMetadataLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function metaColumnId(key: string): string {
  return `meta_${key}`;
}

export function useDynamicMetadataColumns<T extends Record<string, unknown>>({
  rows,
  metadataAccessor,
  hiddenKeys = DEFAULT_HIDDEN_KEYS,
  knownKeys,
  labelOverrides,
  sortable = true,
}: DynamicMetadataColumnsOptions<T>): DynamicMetadataColumnsResult<T> {
  const metadataKeys = useMemo(() => {
    const keySet = new Set<string>(
      knownKeys?.filter((k) => !hiddenKeys.has(k)),
    );
    for (const row of rows) {
      const meta = metadataAccessor(row);
      if (meta) {
        for (const key of Object.keys(meta)) {
          if (!hiddenKeys.has(key)) {
            keySet.add(key);
          }
        }
      }
    }
    return Array.from(keySet).sort();
  }, [rows, metadataAccessor, hiddenKeys, knownKeys]);

  const metadataColumns: TableColumn<T>[] = useMemo(
    () =>
      metadataKeys.map((key) => ({
        id: metaColumnId(key) as keyof T,
        label: labelOverrides?.[key] ?? formatMetadataLabel(key),
        sortable,
        render: (value: unknown) => {
          if (value === null || value === undefined || value === '') return '-';
          if (typeof value === 'object') return JSON.stringify(value);
          return String(value);
        },
      })),
    [metadataKeys, labelOverrides, sortable],
  );

  const flattenRow = useMemo(() => {
    return (row: T): T => {
      const meta = metadataAccessor(row);
      if (!meta) return row;
      const flattened: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(meta)) {
        if (!hiddenKeys.has(key)) {
          flattened[metaColumnId(key)] = value;
        }
      }
      return { ...row, ...flattened } as T;
    };
  }, [metadataAccessor, hiddenKeys]);

  return { metadataKeys, metadataColumns, flattenRow };
}
